import { useState } from "react";
import axios from "axios";

type CustomerData = {
  name: string;
  email: string;
};

type StripeCustomer = {
  id: string;
  name: string | null;
  email: string | null;
  created: number;
};

type UseCreateCustomerReturn = {
  createCustomer: (customerData: CustomerData) => Promise<StripeCustomer | null>;
  loading: boolean;
  error: string | null;
  customer: StripeCustomer | null;
};

const useCreateCustomer = (): UseCreateCustomerReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<StripeCustomer | null>(null);

  const createCustomer = async (customerData: CustomerData): Promise<StripeCustomer | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        "https://api.stripe.com/v1/customers",
        new URLSearchParams({
          name: customerData.name,
          email: customerData.email,
        }),
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const newCustomer: StripeCustomer = response.data;
      setCustomer(newCustomer);
      return newCustomer;
    } catch (err) {
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.error?.message || err.message
        : "An unknown error occurred";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createCustomer,
    loading,
    error,
    customer,
  };
};

export default useCreateCustomer;
